import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    Typography,
    LinearProgress,
    Stack,
    Chip,
    IconButton,
    Collapse,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Paper
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Description as DocumentIcon,
    HourglassEmpty as ProcessingIcon,
    CheckCircle as CompleteIcon,
    Error as ErrorIcon
} from '@mui/icons-material';
import { useSocket } from '../hooks/useSocket';

interface ProcessingDocument {
    documentId: string;
    fileName: string;
    status: 'queued' | 'processing' | 'completed' | 'failed';
    progress: number;
    timestamp: Date;
    error?: string;
}

const ProcessingDocuments: React.FC = () => {
    const [documents, setDocuments] = useState<Map<string, ProcessingDocument>>(new Map());
    const [expanded, setExpanded] = useState(true);
    const { on, off } = useSocket();

    useEffect(() => {
        const handleDocumentQueued = (data: any) => {
            setDocuments(prev => {
                const newMap = new Map(prev);
                newMap.set(data.documentId, {
                    documentId: data.documentId,
                    fileName: data.fileName,
                    status: 'queued',
                    progress: 0,
                    timestamp: new Date(data.timestamp)
                });
                return newMap;
            });
        };

        const handleDocumentProcessing = (data: any) => {
            setDocuments(prev => {
                const newMap = new Map(prev);
                const existing = newMap.get(data.documentId);
                if (existing) {
                    newMap.set(data.documentId, {
                        ...existing,
                        status: 'processing',
                        progress: data.progress || 0
                    });
                }
                return newMap;
            });
        };

        const handleDocumentCompleted = (data: any) => {
            setDocuments(prev => {
                const newMap = new Map(prev);
                const existing = newMap.get(data.documentId);
                if (existing) {
                    newMap.set(data.documentId, {
                        ...existing,
                        status: 'completed',
                        progress: 100
                    });
                }
                return newMap;
            });

            // Remove completed documents after 5 seconds
            setTimeout(() => {
                setDocuments(prev => {
                    const newMap = new Map(prev);
                    newMap.delete(data.documentId);
                    return newMap;
                });
            }, 5000);
        };

        const handleDocumentFailed = (data: any) => {
            setDocuments(prev => {
                const newMap = new Map(prev);
                const existing = newMap.get(data.documentId);
                if (existing) {
                    newMap.set(data.documentId, {
                        ...existing,
                        status: 'failed',
                        progress: 0,
                        error: data.error
                    });
                }
                return newMap;
            });
        };

        on('document:queued', handleDocumentQueued);
        on('document:processing', handleDocumentProcessing);
        on('document:completed', handleDocumentCompleted);
        on('document:failed', handleDocumentFailed);

        return () => {
            off('document:queued', handleDocumentQueued);
            off('document:processing', handleDocumentProcessing);
            off('document:completed', handleDocumentCompleted);
            off('document:failed', handleDocumentFailed);
        };
    }, [on, off]);

    const processingDocs = Array.from(documents.values());
    
    if (processingDocs.length === 0) {
        return null;
    }

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'queued':
                return <ProcessingIcon fontSize="small" />;
            case 'processing':
                return <ProcessingIcon fontSize="small" className="animate-spin" />;
            case 'completed':
                return <CompleteIcon fontSize="small" color="success" />;
            case 'failed':
                return <ErrorIcon fontSize="small" color="error" />;
            default:
                return <DocumentIcon fontSize="small" />;
        }
    };

    const getStatusColor = (status: string): 'default' | 'primary' | 'success' | 'error' => {
        switch (status) {
            case 'queued':
                return 'default';
            case 'processing':
                return 'primary';
            case 'completed':
                return 'success';
            case 'failed':
                return 'error';
            default:
                return 'default';
        }
    };

    return (
        <Paper
            sx={{
                position: 'fixed',
                bottom: 16,
                right: 16,
                width: 400,
                maxHeight: 400,
                zIndex: 1100,
                boxShadow: 3
            }}
            elevation={3}
        >
            <Box
                sx={{
                    p: 2,
                    borderBottom: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer'
                }}
                onClick={() => setExpanded(!expanded)}
            >
                <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="subtitle1" fontWeight="medium">
                        Document Processing
                    </Typography>
                    <Chip
                        label={processingDocs.length}
                        size="small"
                        color="primary"
                    />
                </Stack>
                <IconButton size="small">
                    {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
            </Box>

            <Collapse in={expanded}>
                <List sx={{ maxHeight: 300, overflow: 'auto', p: 0 }}>
                    {processingDocs.map((doc) => (
                        <ListItem key={doc.documentId} divider>
                            <ListItemText
                                primary={
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        {getStatusIcon(doc.status)}
                                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                            {doc.fileName}
                                        </Typography>
                                    </Stack>
                                }
                                secondary={
                                    <Box sx={{ mt: 1 }}>
                                        {doc.status === 'processing' && (
                                            <LinearProgress
                                                variant="determinate"
                                                value={doc.progress}
                                                sx={{ mb: 0.5 }}
                                            />
                                        )}
                                        <Stack direction="row" spacing={1}>
                                            <Chip
                                                label={doc.status}
                                                size="small"
                                                color={getStatusColor(doc.status)}
                                            />
                                            {doc.error && (
                                                <Typography variant="caption" color="error">
                                                    {doc.error}
                                                </Typography>
                                            )}
                                        </Stack>
                                    </Box>
                                }
                            />
                        </ListItem>
                    ))}
                </List>
            </Collapse>
        </Paper>
    );
};

export default ProcessingDocuments;